import './scss/StatsView.scss';
import {FormattedMessage, useIntl} from "react-intl";
import React, {Fragment, useEffect, useMemo, useState} from 'react'
import {
    GetArchivedAndFinishedSurveys, GetCustomerSegment,
    GetFollowupSentiments,
    GetFollowupTemplates,
    GetSelectOptions,
    GetStats,
    GetStatsConfig, GetUseCases
} from "./API";
import {Skeleton, MultiSelect, HoverTooltip, NestedDropdown} from "./UIElements";
import {useLocation, useNavigate} from "react-router-dom";
import { useTable, useSortBy } from 'react-table'
import {ReactComponent as ChartIcon} from "./svg/bar-chart.svg";
import {ReactComponent as TableIcon} from "./svg/table.svg";
import {ReactComponent as AlertIcon} from "./svg/snack-error.svg";
import {
    calculateFollowupRates,
    calculateTotalValues,
    findLimit,
    getSentimentColor,
    groupResultsBySentimentValue, isResultText,
    shadeColor, sortBySentiment, sortSupportTypesByWorstResponseRate, sortThemesByWorstResponse, useMediaQuery
} from "./formats";
import {DateRange} from "./DateRange";
import FilterSummary from "./FilterSummary";
import {getFilterLabel, filterUseCasesBySegment, getFilterTitle} from './filterUtils';
import {useCurrentUserData} from "./App";

export default function StatsViewContainer() {
    const [campaigns, setCampaigns] = useState([])
    const [surveys, setSurveys] = useState([])
    const [locations, setLocations] = useState([])
    const [degrees, setDegrees] = useState([])
    const [groups, setGroups] = useState([])
    const [useCases, setUseCases] = useState([])
    const [dateRange, setDateRange] = useState([])

    const getDefaultQuarter = () => {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1;

        // Determine quarter based on month (using month - 1 logic)
        const monthForQuarter = currentMonth === 1 ? 12 : currentMonth - 1;
        const quarterMonth = Math.floor((monthForQuarter - 1) / 3) * 3 + 1;
        const quarterYear = monthForQuarter === 12 ? currentYear - 1 : currentYear;

        const startDate = `${quarterYear}-${String(quarterMonth).padStart(2, '0')}-01`;
        const endMonth = quarterMonth + 2;
        const endYear = endMonth > 12 ? quarterYear + 1 : quarterYear;
        const adjustedEndMonth = endMonth > 12 ? endMonth - 12 : endMonth;
        const endDate = `${endYear}-${String(adjustedEndMonth).padStart(2, '0')}-${adjustedEndMonth === 12 ? '31' : '30'}`;

        return `${startDate}_${endDate}`;
    };

    const filtersSet = campaigns?.length > 0 || locations?.length > 0 || degrees?.length > 0 || groups?.length > 0 || surveys?.length > 0 || useCases?.length > 0 || dateRange?.length > 0

    const getStats = GetStats(campaigns, locations, degrees, groups, surveys, useCases, dateRange)

    const navigate = useNavigate()
    const urlLocation = useLocation()

    const encodeArray = (arr) => encodeURIComponent(arr.join("||"))
    const decodeArray = (str) => str ? decodeURIComponent(str).split("||") : []

    useEffect(() => {
        const queryParams = new URLSearchParams(urlLocation.search);
        setCampaigns(decodeArray(queryParams.get("campaign")));
        setSurveys(decodeArray(queryParams.get("survey")));
        setUseCases(decodeArray(queryParams.get("usecase")));
        setLocations(decodeArray(queryParams.get("location")));
        setDegrees(decodeArray(queryParams.get("degree")));
        setGroups(decodeArray(queryParams.get("group")))
        setDateRange(decodeArray(queryParams.get("daterange")));

        // Set default date range only if no parameters exist in URL
        const hasDefaultBeenSet = sessionStorage.getItem('dateRangeSet');
        const hasAnyParams = Array.from(queryParams.keys()).length > 0;

        if (!hasAnyParams && !hasDefaultBeenSet) {
            const defaultQuarter = getDefaultQuarter();
            const newParams = new URLSearchParams(queryParams);
            newParams.set("daterange", defaultQuarter);
            navigate(`${urlLocation.pathname}?${newParams.toString()}`);
            sessionStorage.setItem('dateRangeSet', 'true');
        }

    }, [urlLocation.search])

    const handleFilterChange = (paramsToUpdate) => {
        const queryParams = new URLSearchParams(urlLocation.search)

        for (const [param, value] of Object.entries(paramsToUpdate)) {
            if (value?.length > 0) {
                queryParams.set(param, encodeArray(value))
            } else {
                queryParams.delete(param)
            }
        }

        navigate(`${urlLocation.pathname}?${queryParams.toString()}`)
    }

    const clearFilters = () => {
        // Clear all query parameters from URL
        navigate(urlLocation.pathname)
    }

    const getStatsConfig = GetStatsConfig()
    const getFilterOptions = GetSelectOptions()
    const getUseCases =  GetUseCases()
    const getSurveys = GetArchivedAndFinishedSurveys()
    const getFollowupSentiments = GetFollowupSentiments()
    const getFollowupTemplates = GetFollowupTemplates()
    const getCustomerSegment = GetCustomerSegment()
    const getStatsIsSuccess = getStats.isSuccess && getStatsConfig.isSuccess && getFollowupSentiments.isSuccess && getFollowupTemplates.isSuccess
    const loadingError = getStats.isError || getStatsConfig.isError || getFilterOptions.isError || getFollowupSentiments.isError || getFollowupTemplates.isError || getUseCases.isError || getCustomerSegment.isError
    const loadingStats = getStats.isLoading || getStatsConfig.isLoading || getFollowupSentiments.isLoading || getFollowupTemplates.isLoading
    const loadingFilters = getFilterOptions.isLoading || getSurveys.isLoading || getUseCases.isLoading || getCustomerSegment.isLoading
    const filtersDoneLoading = getFilterOptions.isSuccess && getSurveys.isSuccess && getUseCases.isSuccess && getCustomerSegment.isSuccess

    return <div className={"stats-view-container"}>
        {loadingFilters &&
            <LoadingFilters />
        }
        {filtersDoneLoading &&
        <StatsFilters
            campaigns={campaigns}
            locations={locations}
            degrees={degrees}
            groups={groups}
            surveys={surveys}
            useCases={useCases}
            onFilterChange={handleFilterChange}
            filterOptions={getFilterOptions?.data}
            surveyConfig = {getSurveys?.data}
            useCaseData={getUseCases?.data[0]}
            customerSegment={getCustomerSegment?.data[0]?.value}
        />
        }
        <DateRange
            dateRange={dateRange}
            onFilterChange={handleFilterChange}
        />
        {filtersDoneLoading &&
            <FilterSummary
                campaigns={campaigns}
                surveys={surveys}
                useCases={useCases}
                locations={locations}
                degrees={degrees}
                groups={groups}
                dateRange={dateRange}
                onClearFilters={clearFilters}
                surveyConfig = {getSurveys?.data}
                useCaseData={getUseCases?.data[0]}
                customerSegment={getCustomerSegment?.data[0]?.value}
            />
        }
        {getStatsIsSuccess &&
            <StatsView getStats={getStats} getStatsConfig={getStatsConfig} filtersSet={filtersSet} getFollowupSentiments={getFollowupSentiments} getFollowupTemplates={getFollowupTemplates}/>
        }
        {loadingError &&
            <LoadingError />
        }
        {loadingStats &&
            <LoadingStats />
        }
    </div>
}

function LoadingStats() {
    return <div className={"stats-view"}>
        {
            [...Array(4)].map( (e, i) => {
                return <div className={"stats-module"} key={i}>
                    <Skeleton height={20} width={70} marginBottom={12}/>
                    <Skeleton height={25} width={200} marginBottom={10}/>
                    <Skeleton height={17} width={150}/>
                </div>
            })
        }</div>
}

function LoadingFilters() {
    const intl = useIntl()
    return <div className={"filters"}>
        {
            [...Array(4)].map( (e, i) => {
                return <div className={"multi-select"} key={i}>
                    <div className={"select"}>
                        {intl.formatMessage({id: "loading", defaultMessage: "Loading..."})}
                    </div>
                </div>
            })
        }
    </div>
}

function StatsFilters({ campaigns, surveys, locations, degrees, groups, useCases, onFilterChange, filterOptions, surveyConfig, useCaseData = [], customerSegment = ""}) {
    const intl = useIntl();
    const utils = { surveyConfig, useCaseData, customerSegment, intl };

    const handleSelectChange = (param, values) => {
        if (param === "campaign") {
            const selectedCampaigns = values.filter(value => {
                const option = combinedOptions.find(option => option.value === value);
                return option && option.type === 'campaign';
            });
            const selectedSurveys = values.filter(value => {
                const option = combinedOptions.find(option => option.value === value);
                return option && option.type === 'survey';
            });

            onFilterChange({
                campaign: selectedCampaigns,
                survey: selectedSurveys
            });
        } else {
            const updateObject = {};
            updateObject[param] = values;
            onFilterChange(updateObject);
        }
    };

    const getLatestEndTimeForCampaign = (campaignName) => {
        const relatedSurveys = surveyConfig.filter(survey => survey?.config?.campaign === campaignName);
        if (relatedSurveys.length === 0) return null;
        return relatedSurveys.reduce((latest, survey) => {
            return latest > survey?.endtime ? latest : survey?.endtime;
        }, relatedSurveys[0]?.endtime);
    };

    const campaignData = filterOptions?.campaign?.filter(campaign => campaign.trim() !== "") || [];
    const campaignOptions = campaignData.map((campaign) => ({
        value: campaign,
        label: getFilterLabel(campaign, 'campaigns', utils),
        endtime: getLatestEndTimeForCampaign(campaign),
        type: 'campaign'
    }));

    const surveyOptions = filterOptions?.survey?.map((surveyId) => ({
        value: surveyId,
        label: getFilterLabel(surveyId, 'surveys', utils),
        endtime: surveyConfig.find(obj => obj.id === surveyId)?.endtime,
        type: 'survey'
    })) || [];

    const combinedOptions = [...campaignOptions, ...surveyOptions].sort((a, b) => {
        if (a.endtime < b.endtime) return 1;
        if (a.endtime > b.endtime) return -1;
        return a.label.localeCompare(b.label);
    });

    const locationOptions = (filterOptions?.location || []).map((location) => ({
        value: location,
        label: getFilterLabel(location, 'locations', utils)
    }));

    const degreeOptions = (filterOptions?.degree || []).map((degree) => ({
        value: degree,
        label: getFilterLabel(degree, 'degrees', utils)
    }));

    const groupOptions = (filterOptions?.group|| []).map((group) => ({
        value: group,
        label: getFilterLabel(group, 'groups', utils)
    }));

    const useCaseOptions = Object.keys(filterUseCasesBySegment(useCaseData, customerSegment))
        .map((usecase) => ({
            value: usecase,
            label: getFilterLabel(usecase, 'useCases', utils),
            title: getFilterTitle(usecase, 'useCases', utils)
        }));

    return <div className={"filters"}>
        <MultiSelect
            options={useCaseOptions}
            selectedValues={useCases}
            onSelectChange={(values) => handleSelectChange("usecase", values)}
            placeholder={intl.formatMessage({id:'selectUseCases', defaultMessage:'Select Use Cases'})}
        />
        <MultiSelect
            options={combinedOptions}
            selectedValues={[...campaigns, ...surveys]}
            onSelectChange={(values) => handleSelectChange("campaign", values)}
            placeholder={intl.formatMessage({id:'selectCampaigns', defaultMessage:'Select Campaigns'})}
        />
        <NestedDropdown placeholder={intl.formatMessage({id:'studentFilters', defaultMessage:'Student Filters'})}>
            <MultiSelect
                options={locationOptions}
                selectedValues={locations}
                onSelectChange={(values) => handleSelectChange("location", values)}
                placeholder={intl.formatMessage({id:'selectLocations', defaultMessage:'Select Locations'})}
            />
            <MultiSelect
                options={degreeOptions}
                selectedValues={degrees}
                onSelectChange={(values) => handleSelectChange("degree", values)}
                placeholder={intl.formatMessage({id:'selectDegrees', defaultMessage:'Select Degrees'})}
            />
            <MultiSelect
                options={groupOptions}
                selectedValues={groups}
                onSelectChange={(values) => handleSelectChange("group", values)}
                placeholder={intl.formatMessage({id:'selectGroups', defaultMessage:'Select Groups'})}
            />
        </NestedDropdown>
    </div>
}

function LoadingError() {
    return <h1>Error in loading stats. Please try again later</h1>
}

function StatsView({getStats, getStatsConfig, filtersSet, getFollowupSentiments, getFollowupTemplates}) {
    const data = getStats?.data
    const followupSentiments = getFollowupSentiments?.data[0] || {}
    const followupTemplates = getFollowupTemplates?.data || {}

    const intl = useIntl()

    const config = useMemo(() => {
        const baseConfig = getStatsConfig?.data[0]?.value
        if (!baseConfig) return null

        return {
            ...baseConfig,
            responseRate: {
                ...baseConfig.responseRate,
                bad: { ...baseConfig.responseRate.bad, name: intl.formatMessage({id:'responseRate.name.bad', defaultMessage:'weak'}) },
                ok: { ...baseConfig.responseRate.ok, name: intl.formatMessage({id:'responseRate.name.ok', defaultMessage:'moderate'}) },
                good: { ...baseConfig.responseRate.good, name: intl.formatMessage({id:'responseRate.name.good', defaultMessage:'excellent'}) }
            },
            supportRate: {
                ...baseConfig.supportRate,
                bad: { ...baseConfig.supportRate.bad, name: intl.formatMessage({id:'supportRate.name.bad', defaultMessage:'unusually high'}) },
                ok: { ...baseConfig.supportRate.ok, name: intl.formatMessage({id:'supportRate.name.ok', defaultMessage:'low'}) },
                good: { ...baseConfig.supportRate.good, name: intl.formatMessage({id:'supportRate.name.good', defaultMessage:'optimal'}) }
            },
            followup: {
                ...baseConfig.followup,
                bad: { ...baseConfig.followup.bad, name: intl.formatMessage({id:'followup.name.bad', defaultMessage:'inadequate'}) },
                ok: { ...baseConfig.followup.ok, name: intl.formatMessage({id:'followup.name.ok', defaultMessage:'satisfactory'}) },
                good: { ...baseConfig.followup.good, name: intl.formatMessage({id:'followup.name.good', defaultMessage:'outstanding'}) }
            }
        }
    }, [getStatsConfig?.data, intl])

    const showResponseRate = data?.responded > 0 && data?.delivered > 0
    const showSupportRate = data?.recipients > 0 && data?.responded > 0
    const showFollowupRate = data?.supportneed > 0 && typeof data?.followuprate === 'number'
    const showSupportThemes = data?.supportneed > 0 && data?.theme
    const showSupportRoles = data?.supportneed > 0 && data?.supporttype
    const showFollowupDetailed = showFollowupRate

    const responseRateErrorText = data?.delivered === 0 ?
        intl.formatMessage({
            defaultMessage: "Response rate cannot be calculated as no messages were delivered.",
            id: "responseDeliveredErrorText"
        }) :
        intl.formatMessage({
            defaultMessage: "Response rate cannot be calculated as there are no responses.",
            id: "responseRespondedErrorText"
        })
    const supportRateErrorText = data?.recipients === 0 ?
        intl.formatMessage({
            defaultMessage: "Support need rate cannot be calculated as the campaign has are no recipients.",
            id: "supportRecipientsErrorText"
        }) :
        intl.formatMessage({
            defaultMessage: "Support need rate cannot be displayed as there are no responses.",
            id: "supportRespondedErrorText"
        })
    const followupRateErrorText = data?.supportneed === 0 ?
        intl.formatMessage({
            defaultMessage: "Support shortfall rate cannot be calculated because no respondents asked for help.",
            id: "followupNoSupportErrorText"
        }) :
        intl.formatMessage({
            defaultMessage: "Follow-up data on support needs is unavailable.",
            id: "followupMissingErrorText"
        })
    const supportThemesErrorText = data?.supportneed ?
        intl.formatMessage({
            defaultMessage: "Cannot display themes because no themes have been assigned to the support needs.",
            id: "noSupportThemesErrorText"
        })
        :
        intl.formatMessage({
            defaultMessage: "Cannot display support need themes because no respondents have asked for help.",
            id: "supportThemesErrorText"
        })
    const supportRolesErrorText = data?.supportneed ?
        intl.formatMessage({
            defaultMessage: "Cannot display support types because no support types have been assigned to the support needs.",
            id: "noSupportRolesErrorText"
        })
        :
        intl.formatMessage({
            defaultMessage: "Cannot display support need by support type because no respondents have asked for help.",
            id: "supportRolesErrorText"
        })
    const followupDetailedErrorText = intl.formatMessage({
        defaultMessage: "Support outcomes by theme cannot be displayed due to insufficient followup data.",
        id: "followupDetailedErrorText"
    })

    const {currentUserData} = useCurrentUserData()
    const userExpired = !currentUserData // null or undefined
    const hostname = window.location.hostname
    const instanceEmail = hostname.indexOf(".") > -1 ? hostname.substring( 0, hostname.indexOf(".") ) +"@annieadvisor.com" : hostname +"@annieadvisor.com"

    if (userExpired) {
        return <div className={"stats-view"}>
            <h2 className={"no-data"}>
                { intl.formatMessage({id:'expiredUser', defaultMessage:'Your user credentials have expired. If you think this is a mistake, please contact'}) }
                {" "}
                <a href={"mailto:"+instanceEmail}>{instanceEmail}.</a>
            </h2>
        </div>
    }

    if (data?.responded === 0 && data?.supportneed === 0 && data?.delivered === 0 && data?.recipients === 0) {
        return <div className={"stats-view"}>
            <h2 className={"no-data"}>
                {filtersSet ?
                    intl.formatMessage({id:'noResultsFound', defaultMessage:'No results found'}) :
                    intl.formatMessage({id:'noDataAvailable', defaultMessage:'No data available'})
                }
            </h2>
        </div>
    }
    //theme.LC in translate general
    //role.SPED in translate general
    return <div className={"stats-view"}>
        {showResponseRate ?
            <RateBlock
                block={"responseRate"}
                data={data}
                config={config?.responseRate}
                rate={(data?.responded && data?.delivered) ? Math.round(data?.responded / data?.delivered * 100) : null}
            /> :
            <ErrorBlock
                block={"responseRate"}
                errorText={responseRateErrorText}
            />
        }
        {showSupportRate ?
            <RateBlock
                block={"supportRate"}
                data={data}
                config={config?.supportRate}
                rate={data?.delivered ? Math.round(data?.supportneed / data?.delivered * 100) : null}
            /> :
            <ErrorBlock
                block={"supportRate"}
                errorText={supportRateErrorText}
            />
        }
        {showFollowupRate ?
            <RateBlock
                block={"followup"}
                data={data}
                config={config?.followup}
                rate={typeof data?.followuprate === 'number' ? Math.round(data?.followuprate  * 100) : null}
            /> :
            <ErrorBlock
                block={"followup"}
                errorText={followupRateErrorText}
            />
        }
        {showSupportThemes ?
            <BarBlock
                block={"supportThemes"}
                data={data}
                supportNeeds={data?.supportneed}
                prefix={"theme"}
                prefixForTranslate={"theme"}
            /> :
            <ErrorBlock
                block={"supportThemes"}
                errorText={supportThemesErrorText}
            />
        }
        {showSupportRoles ?
            <BarBlock
                block={"supportRoles"}
                data={data}
                supportNeeds={data?.supportneed}
                prefix={"supporttype"}
                prefixForTranslate={"role"}
            /> :
            <ErrorBlock
                block={"supportRoles"}
                errorText={supportRolesErrorText}
            />
        }
        {showFollowupDetailed ?
            <DivergingBarBlock
                block={"followupDetailed"}
                data={data}
                config={config?.followup}
                sentiments={followupSentiments}
                templates={followupTemplates}
            /> :
            <ErrorBlock
                block={"followupDetailed"}
                errorText={followupDetailedErrorText}
            />
        }
    </div>
}

function DivergingBarBlock({ block, data, config, sentiments, templates }) {
    const intl = useIntl()
    const activeViewFromStorage = localStorage.getItem("followupActiveView")
    const [activeView, setActiveView] = useState(activeViewFromStorage ? activeViewFromStorage : "theme")

    const responseRate = data?.followupdelivered && data?.followupcompleted ? Math.round(data?.followupcompleted / data?.followupdelivered * 100) + "%" : "N/A"
    const rate = data?.followuprate ? Math.round(data?.followuprate  * 100) : "N/A"

    const level = findLimit(config, rate)
    const levelName = level ? level?.name : "N/A"
    const levelStatus = level ? level?.status : ""
    const localeMessages = intl?.messages

    function filterTexts(array) {
        const regex = new RegExp(`^${block}\\.text\\.(${levelStatus}\\.\\d+|\\d+)$`)
        return array.filter((text) => regex.test(text))
    }
    const blockTexts = filterTexts(Object.keys(localeMessages))

    const sortedTexts = sortBlockTexts(blockTexts)

    const handleViewToggle = (view) => {
        setActiveView(view)
        localStorage.setItem("followupActiveView", view)
    }

    const availableSentiments = Object.keys(sentiments)

    const filteredData = {
        ...data,
        theme: data?.theme ? Object.fromEntries(
            Object.entries(data.theme).filter(([key]) => key !== '')
        ) : data?.theme
    }

    const chartDataMap = {
        /*
        all: {
            all: {
                followupresult: filteredData?.followupresult
            }
        },
         */
        theme: filteredData?.theme,
        supporttype: filteredData?.supporttype
    }
    const viewOptions = Object.keys(chartDataMap)
    const dataForChart = chartDataMap[activeView] || data?.followupresult

    const bestSupportTypes = calculateFollowupRates(data, sentiments, activeView)
    const filteredBestSupport = bestSupportTypes.filter(obj => obj?.rate > .5)
    const [displayAmountBestSupport, setDisplayAmountBestSupport] = useState(filteredBestSupport?.length > 1 ? filteredBestSupport?.length > 3 ? 3 : filteredBestSupport?.length : 1)
    const displayAmountShorterThanTotalSupport = displayAmountBestSupport < bestSupportTypes?.length

    const lowestResponseSupportTypes = sortSupportTypesByWorstResponseRate(data, activeView)
    const filteredLowestResponse = lowestResponseSupportTypes.filter(obj => obj?.rate < .5)
    const [displayAmountLowestResponse, setDisplayAmountLowestResponse] = useState(filteredLowestResponse?.length > 1 ? filteredLowestResponse?.length > 3 ? 3 : filteredLowestResponse?.length : 1)
    const displayAmountShorterThanTotalResponse = displayAmountLowestResponse < lowestResponseSupportTypes?.length

    // Filter out variables from text block if nothing to render
    let variablesMissingContent = []
    if (!lowestResponseSupportTypes?.length > 0) variablesMissingContent.push('{worstResponse}')
    if (!bestSupportTypes?.length > 0) variablesMissingContent.push('{bestSupport}')
    let removeKeys = []
    for (let key in variablesMissingContent) {
        const search = variablesMissingContent[key]
        const results = Object.keys(localeMessages).filter(key => localeMessages[key].includes(search))
        if (results.length) removeKeys = [...removeKeys, ...results]
    }
    const filteredTexts = sortedTexts.filter(text => !removeKeys.includes(text))

    const translatePrefix = activeView === "supporttype" ? "role" : activeView

    const unknownDataText = intl.formatMessage({
        defaultMessage: 'Unknown',
        id: 'unknown'
    })

    return <StatsModule>
        <StatsTexts>
            <h2>
                <div className={"texts"}>
                    <div className={"header"}>
                        {intl.formatMessage({id:block+'.title', defaultMessage:block})}
                    </div>
                </div>
            </h2>
            {filteredTexts?.length < 1 ?
                <p className={"no-data"}>
                    {intl.formatMessage({id:'noDataAvailable', defaultMessage:'No data available'})}
                </p> :
                <ul>
                    {filteredTexts.map((text, i) => {
                        return <li key={i} className={isResultText(text) ? "result" : ''}>
                            <FormattedMessage
                                id={text}
                                defaultMessage={localeMessages[text] ? localeMessages[text] : text}
                                values={{
                                    levelName: <b>{levelName}</b>,
                                    rate: <b>{rate}%</b>,
                                    responseRate: <b>{responseRate}</b>,
                                    b: (text) => <b>{text}</b>,
                                    bestSupport: <ul>
                                        {bestSupportTypes.slice(0,displayAmountBestSupport).map((support,i) => {
                                            const result = Math.round(support?.rate * 100) + "%"
                                            const asterix = support?.prefix ? '*' : ''
                                            const tooltipText = support?.prefix ?
                                                `${intl.formatMessage({
                                                    defaultMessage:'The true result is {prefix}{result}.\nThe exact value is obfuscated for security reasons.',
                                                    id: 'trueResultPercentage'
                                                }, {
                                                    prefix: support?.prefix,
                                                    result: result
                                                })}\n
                                                ${intl.formatMessage({
                                                    defaultMessage:'{amount} respondents',
                                                    id: 'amountOfRespondents'
                                                }, {
                                                    amount: support?.amount
                                                })}` :
                                                `${intl.formatMessage({
                                                    defaultMessage:'{amount} respondents',
                                                    id: 'amountOfRespondents'
                                                }, {
                                                    amount: support?.amount
                                                })}`
                                            return <li key={i}>
                                                {intl.formatMessage({
                                                    defaultMessage: support?.supportType || unknownDataText,
                                                    id: `${translatePrefix}.${support?.supportType}`
                                                })}
                                                <b>
                                                    {' '}
                                                    <HoverTooltip className={"inline"} text={tooltipText}>
                                                        {result}{asterix}
                                                    </HoverTooltip>
                                                </b>
                                            </li>
                                        })}
                                    </ul>,
                                    worstResponse: <ul>
                                        {lowestResponseSupportTypes.slice(0,displayAmountLowestResponse).map((support,i) => {
                                            const result = Math.round(support?.rate * 100) + "%"
                                            const asterix = support?.prefix ? '*' : ''
                                            const tooltipText = support?.prefix ?
                                                `${intl.formatMessage({
                                                    defaultMessage:'The true result is {prefix}{result}.\nThe exact value is obfuscated for security reasons.',
                                                    id: 'trueResultPercentage'
                                                }, {
                                                    prefix: support?.prefix,
                                                    result: result
                                                })}\n
                                                ${intl.formatMessage({
                                                    defaultMessage:'{amount} respondents',
                                                    id: 'amountOfRespondents'
                                                }, {
                                                    amount: support?.amount
                                                })}` :
                                                `${intl.formatMessage({
                                                    defaultMessage:'{amount} respondents',
                                                    id: 'amountOfRespondents'
                                                }, {
                                                    amount: support?.amount
                                                })}`
                                            return <li key={i}>
                                                {intl.formatMessage({
                                                    defaultMessage: support?.supportType || unknownDataText,
                                                    id: `${translatePrefix}.${support?.supportType}`
                                                })}
                                                <b>
                                                    {' '}
                                                    <HoverTooltip className={"inline"} text={tooltipText}>
                                                        {result}{asterix}
                                                    </HoverTooltip>
                                                </b>
                                            </li>
                                        })}
                                    </ul>
                                }} />
                        </li>
                    })}
                </ul>
            }
        </StatsTexts>
        <StatsGraph className={"column"}>
            <div className={"switch-toggle"}>
                {viewOptions.map((view, i) => {
                    return <button
                        onClick={() => handleViewToggle(view)}
                        key={view}
                        className={activeView === view ? "active" : ""}
                    >
                        {intl.formatMessage({
                            defaultMessage: view,
                            id: `followup.${view}`
                        })}
                    </button>
                })}
            </div>
            <DivergingBarChart data={dataForChart} followupType={data?.followuptype} activeView={activeView} templates={templates} availableSentiments={availableSentiments} sentiments={sentiments} />
        </StatsGraph>
    </StatsModule>
}

function DivergingBarChart({data, activeView, templates, availableSentiments, sentiments, followupType }) {
    const intl = useIntl()
    const viewingAll = activeView === "all"
    const bars = sortBySentiment(data, sentiments)
    const percentValues = ["-100%","-50","0","50","100%"]

    return <div className={"diverging-chart"}>
        <div className={"headers"}>
            {percentValues.map((value,i) => (
                <p key={i}>{value}</p>
            ))}
        </div>
        <div className={"bars"}>
            {bars.map((bar, i) => {
                let results = {}
                const followupInput = data[bar]?.followupresult || {}
                for (let key in followupInput) {
                    if (followupInput.hasOwnProperty(key)) {
                        // If value is an object with 'count' property
                        if (typeof followupInput[key] === 'object' && followupInput[key].hasOwnProperty('count')) {
                            results[key] = followupInput[key].count
                        }
                        // If value is already a number
                        else if (typeof followupInput[key] === 'number') {
                            results[key] = followupInput[key]
                        }
                    }
                }

                if (!Object.keys(results).length) return null;

                const groupedResults = groupResultsBySentimentValue(results, sentiments);
                const { totalNegative, totalNeutral, totalPositive } = calculateTotalValues(results, sentiments);

                const combinedWidth = 50 // 50% of total bar width
                const negativeWidthPercentage = (totalNegative / (totalNegative + totalNeutral + totalPositive)) * combinedWidth;
                const neutralWidthPercentage = (totalNeutral / (totalNegative + totalNeutral + totalPositive)) * combinedWidth;
                const positiveWidthPercentage = combinedWidth - negativeWidthPercentage - neutralWidthPercentage
                const leftMarginPercentage = positiveWidthPercentage + (neutralWidthPercentage / 2)

                const renderBarSegment = (filterCondition, className) => (
                    Object.keys(groupedResults)
                        .filter(val => filterCondition(Number(val)))
                        .map((sentimentValue) => {
                            const sentimentObj = groupedResults[sentimentValue]
                            const count = sentimentObj?.count
                            const tooltipText = Object.keys(sentimentObj?.followupresult)
                                .map((result, i) => {
                                    const resultCount = sentimentObj?.followupresult[result]
                                    return <p key={i} className={"tooltip-results"}>
                                        {intl.formatMessage({
                                            defaultMessage: result,
                                            id: `followupresult.${result}`
                                        })}
                                        <span className={"amount"}>
                                            {resultCount > 0 && resultCount < 4 && "≤"}
                                            {resultCount}
                                        </span>
                                        </p>
                                })

                            return <div
                                key={sentimentValue}
                                style={{
                                    flexGrow: count,
                                    backgroundColor: getSentimentColor(Number(sentimentValue))
                                }}>
                                <HoverTooltip className={"fill"} key={i} text={tooltipText} />
                            </div>
                        })
                )

                return <React.Fragment key={i}>
                    <div className="bar" style={{ display: 'flex', width: '100%' }}>
                        {!viewingAll &&
                            <span className={"title"}>
                                {intl.formatMessage({
                                    defaultMessage:`${bar}`,
                                    id:`${activeView === "supporttype" ? "role" : activeView}.${bar}`
                            })}
                            </span>
                        }
                        <div className={"negative"} style={{ marginLeft: `${leftMarginPercentage}%`, display: 'flex', width: `${negativeWidthPercentage}%` }}>
                            {renderBarSegment(val => val < 0, "negative")}
                        </div>
                        {neutralWidthPercentage > 0 &&
                            <div className={"neutral"} style={{ display: 'flex', width: `${neutralWidthPercentage}%` }}>
                                {renderBarSegment(val => val === 0, "neutral")}
                            </div>
                        }
                        {positiveWidthPercentage > 0 &&
                            <div className={"positive"} style={{ display: 'flex', width: `${positiveWidthPercentage}%` }}>
                                {renderBarSegment(val => val > 0, "positive")}
                            </div>
                        }
                    </div>
                    {viewingAll && Object.keys(results)?.length > 0 &&
                        <div className={"followup-questions"}>
                            {Object.keys(results)
                                .sort((a,b) => sentiments[b]?.sentiment - sentiments[a]?.sentiment)
                                .map((result,i) => {
                                    if (result === "NORESPONSE") return
                                    const count = results[result]
                                    const sentiment = sentiments[result]?.sentiment

                                    return <div className={"answer"} key={i}>
                                        {intl.formatMessage({
                                            defaultMessage: result,
                                            id: `followupresult.${result}`
                                        })}
                                        <div className={"amount"} style={{
                                            backgroundColor: getSentimentColor(Number(sentiment)),
                                            color: getSentimentColor(Number(sentiment), true)
                                        }}>
                                            {count > 0 && count < 4 && "≤"}
                                            {count}
                                        </div>
                                    </div>
                            })}
                        </div>
                    }
                </React.Fragment>
            })}
        </div>
    </div>
}

function GetNameForAbb(value, prefix, prefixForTranslate) {
    const intl = useIntl()
    const prefixRemoved = value
    if (prefixRemoved?.length < 1) {
        return null
    }
    if (prefixForTranslate?.length) {
        if (prefixRemoved === 'SUPPORTPROVIDER' || prefixRemoved === 'INFORMATION') {
            return intl.formatMessage({id:'supportRoles.'+prefixRemoved, defaultMessage:prefixRemoved})
        }
        return intl.formatMessage({id:prefixForTranslate+'.'+prefixRemoved, defaultMessage:prefixRemoved})
    }
    return intl.formatMessage({id:prefix+'.'+prefixRemoved, defaultMessage:prefixRemoved})
}

function sortBlockTexts(arr) {
    if (!arr) {
        return null
    }
    const sortedArr = arr.sort((a, b) => {
        const aDotCount = (a.match(/\./g) || []).length;
        const bDotCount = (b.match(/\./g) || []).length;
        if (aDotCount < bDotCount) {
            return -1;
        } else if (aDotCount > bDotCount) {
            return 1;
        } else {
            const aLastDigit = parseInt(a.match(/\.(\d+)$/)?.[1]);
            const bLastDigit = parseInt(b.match(/\.(\d+)$/)?.[1]);
            if (!aLastDigit && !bLastDigit) {
                return 0;
            } else if (!aLastDigit) {
                return 1;
            } else if (!bLastDigit) {
                return -1;
            } else {
                return aLastDigit - bLastDigit;
            }
        }
    })

    return sortedArr;
}

function BarBlock({block, data, supportNeeds, prefix, prefixForTranslate}) {
    const intl = useIntl()
    const [showTable, setShowTable] = useState(localStorage.getItem('showTable') === 'true')
    useEffect(() => {
        localStorage.setItem('showTable', showTable.toString());
    }, [showTable])

    // If key missing from data
    if (!data.hasOwnProperty(prefix)) {
        return
    }

    const dataObject = data[prefix]
    const dataEntries = Object.entries(dataObject).filter(array => array[0]?.length > 0)
    const sortedEntries = dataEntries.sort((a, b) => b[1]?.count - a[1]?.count)
    const sortedObject = sortedEntries.map(([key, value]) => ({
        [key]: value
    }))
    const themesWithValueZero = Object.keys(dataObject).filter(key => dataObject[key]?.count === 0)

    // filter texts for block
    const localeMessages = intl?.messages
    function filterTexts(array) {
        if (sortedObject?.length > 0) {
            return array.filter(key => key.startsWith(block+".text"))
        }
        const regex = new RegExp(`^${block}\\.text\\.\\d+$`)
        return array.filter(key => regex.test(key))
    }
    const blockTexts = filterTexts(Object.keys(localeMessages))

    const sortedTexts = sortBlockTexts(blockTexts)
    // Filter out variables from text block if nothing to render
    let variablesMissingContent = []
    // conditions to remove key
    if (!themesWithValueZero?.length > 0) variablesMissingContent.push('{missingThemes}')
    let removeKeys = []
    for (let key in variablesMissingContent) {
        const search = variablesMissingContent[key]
        const results = Object.keys(localeMessages).filter(key => localeMessages[key].includes(search))
        if (results.length) removeKeys = [...removeKeys, ...results]
    }
    const filteredTexts = sortedTexts.filter(text => !removeKeys.includes(text))

    const roleAmounts = block === "supportRoles"

    const handleTableToggle = () => {
        setShowTable(!showTable)
    }

    return <StatsModule>
        <StatsTexts>
            <h2>
                <div className={"header"}>
                    {intl.formatMessage({id:block+'.title', defaultMessage:block})}
                </div>
            </h2>
            {sortedObject?.length < 1 ?
            <p className={"no-data"}>
                {intl.formatMessage({id:'noDataAvailable', defaultMessage:'No data available'})}
            </p> :
                <ul>
                    {filteredTexts.map((text, i) => {
                        const mostFrequent = GetNameForAbb(Object.keys(sortedObject[0])[0], prefix, prefixForTranslate)
                        let frequency = Object.values(sortedObject[0])[0].count
                        const displayFrequency = frequency <= 3 ? '≤3' : frequency
                        frequency = frequency <= 3 ? 3 : frequency
                        const percentage = "(" + Math.round(frequency / supportNeeds * 100) + "%)"
                        const displayPercentage = frequency <= 3 ? `(≤${Math.round(frequency / supportNeeds * 100)}%)` : percentage;
                        return <li key={i} className={isResultText(text) ? "result" : ''}>
                            <FormattedMessage
                                id={text}
                                defaultMessage={localeMessages[text] ? localeMessages[text] : text}
                                values={{
                                    mostFrequent: <b>{mostFrequent}</b>,
                                    frequency: <b>{displayFrequency}</b>,
                                    percentage: displayPercentage,
                                    b: (text) => <b>{text}</b>,
                                    missingThemes: <ul>
                                        {themesWithValueZero.map((theme,i) => {
                                            return <li key={i}>
                                                {GetNameForAbb(theme, prefix, prefixForTranslate)}
                                            </li>
                                        })}
                                    </ul>
                                }} />
                        </li>
                    })}
                </ul>
            }
        </StatsTexts>
        <StatsGraph className={"column"}>
            <div className={"switch-toggle"}>
                <button onClick={handleTableToggle} className={!showTable ? "active" : ""} title={"Show bar chart view"}>
                    <ChartIcon />
                </button>
                <button onClick={handleTableToggle} className={showTable ? "active" : ""} title={"Show table view"}>
                    <TableIcon />
                </button>
            </div>
            {!showTable ?
                <BarChart data={sortedObject} prefix={prefix} supportNeeds={supportNeeds} prefixForTranslate={prefixForTranslate} roleAmounts={roleAmounts}/>
                :
                <RenderTable dataArray={sortedObject} roleAmounts={roleAmounts} prefix={prefix} prefixForTranslate={prefixForTranslate}/>
            }
        </StatsGraph>
    </StatsModule>
}

function RenderTable({ dataArray, roleAmounts, prefix, prefixForTranslate }) {
    const intl = useIntl()
    const resultArray = []
    dataArray.forEach((item) => {
        const supportTypeKey = Object.keys(item)[0]
        const supportType = supportTypeKey
        const supportNeeds = item[supportTypeKey]?.count
        const providerKey = supportType
        const providerAmount = item[providerKey]?.providers ?? "-"

        const needsPerProvider = providerAmount !== "-" ? supportNeeds / providerAmount : "-";

        resultArray.push({
            providerName: supportTypeKey,
            supportNeeds: supportNeeds,
            providerAmount: providerAmount,
            needsPerProvider: needsPerProvider,
        });
    })

    const data = React.useMemo(
        () => resultArray, []
    )

    const columns = React.useMemo(() => {
        if (!roleAmounts) {
            return [
                {
                    Header: intl.formatMessage({id:'supportTheme',defaultMessage:'Support theme'}),
                    accessor: 'providerName',
                    Cell: ({ value }) => GetNameForAbb(value, prefix, prefixForTranslate)
                },
                {
                    Header: intl.formatMessage({id:'supportNeeds', defaultMessage:'Support needs'}),
                    accessor: 'supportNeeds',
                    Cell: ({ value }) => (
                        <span>{value < 4 && value > 0 && value !== "-" ? '≤' : ''}{value}</span>
                    ),
                },
            ]
        } else {
            return [
                {
                    Header: intl.formatMessage({id:'supportType',defaultMessage:'Support type'}),
                    accessor: 'providerName',
                    Cell: ({ value }) => GetNameForAbb(value, prefix, prefixForTranslate)
                },
                {
                    Header: intl.formatMessage({id:'people', defaultMessage:'People'}),
                    accessor: 'providerAmount',
                },
                {
                    Header: intl.formatMessage({id:'supportNeeds', defaultMessage:'Support needs'}),
                    accessor: 'supportNeeds',
                    Cell: ({ value }) => (
                        <span>{value < 4 && value > 0 && value !== "-" ? '≤' : ''}{value}</span>
                    ),
                },
                {
                    Header: intl.formatMessage({id:'perPerson', defaultMessage:'per person'}),
                    accessor: 'needsPerProvider',
                    Cell: ({ value, row }) => (
                        <span>
                            {row.values.supportNeeds < 4 && row.values.supportNeeds >0 && value !== "-" ? '≤' : ''}
                            {value !== "-" ? ((value % 1 === 0) ? value.toString() : value.toFixed(1)) : value}
                        </span>
                    ),
                },
            ]
        }
        }, [roleAmounts]
    )

    return <Table data={data} columns={columns} />
}

function Table({ data, columns }) {
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
    } = useTable({ columns, data }, useSortBy);

    return (
        <table {...getTableProps()}>
            <thead>
            {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map(column => (
                        <th
                            {...column.getHeaderProps(column.getSortByToggleProps())}
                            className={column.isSorted
                                ? column.isSortedDesc
                                    ? 'sorted-desc'
                                    : 'sorted-asc'
                                : ''}
                        >
                            {column.render('Header')}
                        </th>
                    ))}
                </tr>
            ))}
            </thead>
            <tbody {...getTableBodyProps()}>
            {rows.map(row => {
                prepareRow(row);
                return (
                    <tr {...row.getRowProps()}>
                        {row.cells.map(cell => {
                            return (
                                <td {...cell.getCellProps()}>
                                    {cell.render('Cell')}
                                </td>
                            )
                        })}
                    </tr>
                )
            })}
            </tbody>
        </table>
    )
}

function BarChart({data, prefix, supportNeeds, prefixForTranslate, roleAmounts}) {
    const intl = useIntl()
    const colors = ["#BDCFFB","#ABD4CC","#FFCAA7", "#FEBACF", "#f4d691"]

    const renderBarSections = (barName, roleCount, bgColor, barValue) => {
        if (roleAmounts && (barName !== "supporttypeINFORMATION" || barName !== "supporttypeSUPPORTPROVIDER")) {
            return Array.from({ length: roleCount }, (_, j) => (
                <div
                    className="bar-part"
                    key={j}
                    style={{
                        width: 100 / roleCount + "%",
                        backgroundColor: bgColor,
                    }}
                />
            ))
        }
        return null
    }

    const renderPerPerson = (barName, barValue, roleCount) => {
        if (roleAmounts && roleCount && barName !== "supporttypeINFORMATION" && barName !== "supporttypeSUPPORTPROVIDER") {
            const perPersonAmount = barValue / roleCount
            const rounded = (perPersonAmount % 1 === 0) ? perPersonAmount.toString() : perPersonAmount.toFixed(1)
            if (barValue < 0 && barValue <= 3) {
                return <FormattedMessage
                    id={'perPersonAmountUnderFour'}
                    defaultMessage={"– ≤{n} pp"}
                    values={{
                        n: rounded,
                    }}
                />
            }
            return <FormattedMessage
                id={'perPersonAmount'}
                defaultMessage={"– {n} pp"}
                values={{
                    n: rounded,
                }}
                />
        }
        return null
    }

    return <div className={"bar-chart"}>
        {data.map((bar, i) => {
            const barName = Object.keys(bar)[0]
            const maxAmount = Object.values(data[0])[0]?.count
            let barValue = bar[barName]?.count
            const displayBarValue = (barValue > 0 && barValue <= 3) ? '≤3' : barValue
            barValue = (barValue > 0 && barValue <= 3) ? 3 : barValue
            const widthPercent = Math.round(barValue / maxAmount * 100)
            const fromTotal =  Math.round(barValue / supportNeeds * 100)
            const displayFromTotal = (barValue > 0 && barValue <= 3) ? `(≤${fromTotal}%)` : `(${fromTotal}%)`
            const colorIndex = i % colors.length
            const bgColor = colors[colorIndex]
            const style = {width: widthPercent+'%', backgroundColor: bgColor}

            const roleCount = roleAmounts && bar[barName]?.providers

            const content = <>
                <div className={"bar-container"}>
                <span className={"title"}>
                    {GetNameForAbb(barName, prefix, prefixForTranslate)} {renderPerPerson(barName, barValue, roleCount)}
                </span>
                    <RenderBar style={style} widthPercent={widthPercent / 100} roleAmounts={roleAmounts} barName={barName} roleCount={roleCount} bgColor={bgColor} barValue={barValue}/>
                </div>
                <div className={"label"}>
                    <b>{displayBarValue}</b> {displayFromTotal}
                </div>
            </>

            if (!roleAmounts) {
                return <div className={"bar-wrap"} key={i}>
                    {content}
                </div>
            }

            const tooltipText =
                `${intl.formatMessage({id:'people', defaultMessage:'People'})}: ${roleCount || '-'}
                ${intl.formatMessage({id:'supportNeeds', defaultMessage:'Support needs'})}: ${displayBarValue}
                ${intl.formatMessage({id:'perPerson', defaultMessage:'per person'})}: ${ barValue > 0 && barValue < 4 && roleCount ? '≤' : ''}${roleCount ? ((barValue / roleCount % 1 === 0) ? (barValue / roleCount).toString() : (barValue / roleCount).toFixed(1)) : '-'}
                `

            return <HoverTooltip className={"bar-wrap"} key={i} text={tooltipText}>
                {content}
            </HoverTooltip>
        })}
    </div>
}

function RenderBar({ style }) {

    return <div className={"bar"} style={style} />
}

function RateBlock({block, data, config, rate}) {
    const intl = useIntl()

    const noData = rate === null
    const level = findLimit(config, rate)
    //const level = config.find(limit => limit.range[0] <= rate && limit.range[1] >= rate)
    const levelName = level ? level?.name : "N/A"
    const levelStatus = level ? level?.status : ""
    const localeMessages = intl?.messages
    // Filter texts for block 'block.text.number' & 'block.text.levelStats.number'
    function filterTexts(array) {
        const regex = new RegExp(`^${block}\\.text\\.(${levelStatus}\\.\\d+|\\d+)$`)
        return array.filter((text) => regex.test(text))
    }
    const blockTexts = filterTexts(Object.keys(localeMessages))

    const sortedTexts = sortBlockTexts(blockTexts)

    const blockMetrics = {
        responseRate: {
            type: () => "responseRate",
            count: (data) => data.delivered,
            // Show alert if less than 30 students received survey
            showAlert: (data) => data?.delivered < 30
        },
        supportRate: {
            // Returns different type based on if we have enough responses but low rate
            type: (data) => {
                const responseRate = data?.responded / data?.delivered
                return data?.responded >= 15 && responseRate < 0.5
                    ? "supportNeedRateLowResponsePercentage" // For low % but enough responses
                    : "supportNeedRate"; // For low response count
            },
            count: (data) => {
                const responseRate = data?.responded / data?.delivered
                return data?.responded >= 15 && responseRate < 0.5
                    ? responseRate // Return rate for low rate cases
                    : data?.responded // Return count for low response cases
            },
            showAlert: (data) => {
                const MIN_RESPONSE_RATE = 0.5
                const MIN_RESPONDED = 15
                return data?.responded < MIN_RESPONDED ||
                    (data?.responded / data?.delivered) < MIN_RESPONSE_RATE
            }
        },
        followup: {
            // Returns different type based on if we have enough responses but low followup rate
            type: (data) => {
                const followupResponseRate = data?.followupcompleted / data?.followupdelivered
                return data?.followupcompleted >= 10 && followupResponseRate < 0.4
                    ? "supportShortfallRateLowResponsePercentage" // For low % but enough responses
                    : "supportShortfallRate"; // For low response count
            },
            count: (data) => {
                const followupResponseRate = data?.followupcompleted / data?.followupdelivered
                return data?.followupcompleted >= 10 && followupResponseRate < 0.4
                    ? followupResponseRate // Return rate for low rate cases
                    : data?.followupcompleted // Return count for low response cases
            },
            showAlert: (data) => {
                const MIN_FOLLOWUP_RESPONSES = 10;
                const MIN_FOLLOWUP_RATE = 0.4
                return data?.followupcompleted < MIN_FOLLOWUP_RESPONSES ||
                    (data?.followupcompleted / data?.followupdelivered) < MIN_FOLLOWUP_RATE
            }
        }
    };

    const blockConfig = blockMetrics[block] || blockMetrics.responseRate

    return <StatsModule>
        {blockConfig.showAlert(data) &&
        <AlertBanner
            responseCount={blockConfig.count(data)}
            alertType={blockConfig.type(data)}
        />
        }
        <StatsTexts>
            <h2>
                <div className={"texts"}>
                    {!noData &&
                        <div className={`${levelStatus} level`}>
                            {levelName}
                        </div>
                    }
                    <div className={"header"}>
                        {intl.formatMessage({id:block+'.title', defaultMessage:block})}
                    </div>
                </div>
                {!noData &&
                    <div className={`${levelStatus} number`}>
                        {rate}<span>%</span>
                    </div>
                }
            </h2>
            {noData ?
                <p className={"no-data"}>
                    {intl.formatMessage({id: 'noDataAvailable', defaultMessage: 'No data available'})}
                </p> :
                <ul>
                    {sortedTexts.map((text, i) => {
                        return <li key={i} className={isResultText(text) ? "result" : ''}>
                            <FormattedMessage
                                id={text}
                                defaultMessage={localeMessages[text] ? localeMessages[text] : text}
                                values={
                                    {
                                        responded: <b>{data?.responded}</b>,
                                        supportneed: <b>{data?.supportneed}</b>,
                                        delivered: <b>{data?.delivered}</b>,
                                        rate: <b>{rate + ' %'}</b>,
                                        levelName: <b>{levelName}</b>,
                                        b: (text) => <b>{text}</b>,
                                        responseRate: <b>{data?.followupdelivered && data?.followupcompleted ? Math.round(data?.followupcompleted / data?.followupdelivered * 100) + "%" : "0"}</b>
                                    }
                                }/>
                        </li>
                    })}
                </ul>
            }
        </StatsTexts>
        <StatsGraph className={"stacked"}>
            <StackedBarGraph config={config} rateLevel={level} rate={rate} totalRange={[0, 100]}/>
        </StatsGraph>
    </StatsModule>
}

function StackedBarGraph({config, rate, rateLevel, totalRange}) {
    const configArray = Object.keys(config).map(key => {
            return {
                'name': config[key]['name'],
                'status': key,
                'min': config[key]['min'],
                'max': config[key]['max'],
            }
        }
    )
    const sorted = configArray.sort((a,b) => (b.max - a.max))
    const rateStyle = {bottom: `${rate}%`}
    return <>
        <div className={"stacked-bar-chart"}>
        {sorted.map( (limit,i) => {
            const lastBar = i === sorted?.length - 1
            const order = i === 0 ? "top" : lastBar ? "bottom" : ""
            const percentage = ((limit.max - limit.min) / (totalRange[1] - totalRange[0])) * 100
            const heightWithTransition = `calc(${percentage}% - 1px)`
            const style = {flex: `0 0 ${heightWithTransition}`}
            const transitionFromTo = lastBar ? "" : limit.status + "-" + sorted[i+1]?.status
            // Last element without transition
            if (lastBar) {
                return <div className={`${limit.status} ${order} bar`} style={style} key={i} />
            }
            return <Fragment key={i}>
                <div className={`${limit.status} ${order} bar`} style={style} />
                <div className={`${transitionFromTo} bar-transition`} />
            </Fragment>
        })}
            <div className={`${rateLevel?.status} rate-arrow`} style={rateStyle} />
    </div>
        <div className={"chart-labels"}>
            {sorted.map( (limit,i) => {
                const percentage = ((limit.max - limit.min) / (totalRange[1] - totalRange[0])) * 100
                const style = {flex: `0 0 ${percentage}%`}
                return <div key={i} style={style}>{limit.max}{i === 0 && " %"}</div>
            })}
        </div>
        </>
}

function StatsModule({children, className}) {
    return <div className={"stats-module"}>
        <div className={`${className ? className : ""} stats-content`}>
            {children}
        </div>
    </div>
}

function StatsTexts({children}) {
    return <div className={"stats-texts"}>
        {children}
    </div>
}

function StatsGraph({children, className}) {
    return <div className={`${className ? className : ""} stats-graph`}>
        <div className={"graph"}>
            {children}
        </div>
    </div>
}

function ErrorBlock({block, errorText}) {
    const intl = useIntl()
    return <div className={"disabled"}>
        <StatsModule>
            <StatsTexts>
                <h2>
                    <div className={"header"}>
                        {intl.formatMessage({id:block+'.title', defaultMessage:block})}
                    </div>
                </h2>
                <p className={"no-data"}>
                    {
                        errorText ?
                            errorText :
                            intl.formatMessage({id: 'noDataAvailable', defaultMessage: 'No data available'})
                    }
                </p>
            </StatsTexts>
        </StatsModule>
    </div>
}

function AlertBanner({ responseCount, alertType }) {
    const [showBanner, setShowBanner] = useState(true)
    const [isMinimized, setIsMinimized] = useState(false);
    const intl = useIntl();
    const responsePercentage = responseCount > 0 && responseCount < 1

    const formattedCount = responsePercentage
        ? `${Math.round(responseCount * 100)}%`
        : responseCount <= 7
            ? '≤7'
            : responseCount;

    const getMessageId = () => {
        const baseId = 'alert.lowCount'
        switch (alertType) {
            case 'responseRate':
                return `${baseId}.receivedCampaign`
            case 'supportNeedRate':
                return `${baseId}.responded`
            case 'supportNeedRateLowResponsePercentage':
                return `${baseId}.responded`
            case 'supportShortfallRate':
                return `${baseId}.answeredFollowup`
            case 'supportShortfallRateLowResponsePercentage':
                return `${baseId}.answeredFollowup`
            default:
                return baseId
        }
    }

    if (!showBanner) return null

    return (
        <div className="alert-banner">
            <div className={`alert-banner__container`}>
                <div className="alert-banner__icon">
                    <AlertIcon />
                </div>
                <div className="alert-banner__text">
                    {responsePercentage ?
                        intl.formatMessage(
                            {
                                id: 'alert.lowCountPercentage',
                                defaultMessage: 'Only {count} of students {action}. Interpret these results with caution.'
                            },
                            {
                                count: formattedCount,
                                action: intl.formatMessage({
                                    id: getMessageId(),
                                    defaultMessage: 'responded'
                                })
                            }
                        ) :
                        intl.formatMessage(
                            {
                                id: 'alert.lowCount',
                                defaultMessage: 'Only {count} students {action}. Interpret these results with caution.'
                            },
                            {
                                count: formattedCount,
                                action: intl.formatMessage({
                                    id: getMessageId(),
                                    defaultMessage: 'responded'
                                })
                            }
                        )
                    }
                    {}
                </div>
                <button className="alert-banner__button" onClick={() => setShowBanner(false)}>
                    {intl.formatMessage({
                        id: 'alert.button.ok',
                        defaultMessage: 'OK'
                    })}
                </button>
            </div>
        </div>
    );
}